import { Box3, Group, type Material, Mesh, type Object3D, type Plane, Vector3 } from "three";
import { type ModelAssetLoader, modelAssetLibrary } from "../models/model-asset-library";
import { ModelAsset } from "../models/model-assets";

const braceRotationX = Math.PI / 2;
const braceRootRotationY = Math.PI / 2;

export class RackShelfSideBrace extends Group {
  private braceHeight = 0;
  private originalLength = 1;

  constructor() {
    super();
    this.name = "rack-shelf-side-brace";
  }

  static async create(loader: ModelAssetLoader = modelAssetLibrary) {
    const sideBrace = new RackShelfSideBrace();
    await sideBrace.load(loader);
    return sideBrace;
  }

  getBraceHeight() {
    return this.braceHeight;
  }

  getOriginalLength() {
    return this.originalLength;
  }

  setLengthScale(scale: number) {
    this.scale.z = scale;
  }

  setClippingPlanes(clipPlanes: Plane[]) {
    this.traverse((child) => {
      if (!(child instanceof Mesh)) {
        return;
      }

      child.material = cloneClippedMaterial(child.material, clipPlanes);
    });
  }

  private async load(loader: ModelAssetLoader) {
    const content = await loader.load(ModelAsset.SideBrace);

    content.rotation.x = braceRotationX;
    enableShadows(content);

    const braceRoot = createAlignedBraceRoot(content);
    this.add(braceRoot);
    this.braceHeight = getObjectHeight(this);
    this.originalLength = getObjectWidth(this);
  }
}

function enableShadows(object: Object3D) {
  object.traverse((child) => {
    if (child instanceof Mesh) {
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });
}

function cloneClippedMaterial(material: Material | Material[], clipPlanes: Plane[]) {
  if (Array.isArray(material)) {
    return material.map((item) => cloneClippedMaterialItem(item, clipPlanes));
  }

  return cloneClippedMaterialItem(material, clipPlanes);
}

function cloneClippedMaterialItem(material: Material, clipPlanes: Plane[]) {
  const clonedMaterial = material.clone();
  clonedMaterial.clippingPlanes = clipPlanes;
  clonedMaterial.clipIntersection = false;
  clonedMaterial.needsUpdate = true;
  return clonedMaterial;
}

function createAlignedBraceRoot(content: Object3D) {
  content.updateWorldMatrix(true, true);
  const bounds = new Box3().setFromObject(content);
  const center = bounds.getCenter(new Vector3());
  const root = new Group();

  content.position.x -= center.x;
  content.position.y -= bounds.min.y;
  content.position.z -= bounds.max.z;
  root.rotation.y = braceRootRotationY;
  root.add(content);
  root.updateWorldMatrix(true, true);

  return root;
}

function getObjectHeight(object: Object3D) {
  object.updateWorldMatrix(true, true);
  return new Box3().setFromObject(object).getSize(new Vector3()).y;
}

function getObjectWidth(object: Object3D) {
  object.updateWorldMatrix(true, true);
  return new Box3().setFromObject(object).getSize(new Vector3()).x;
}
